import { useEffect } from 'react';
import { matchPath, useLocation } from 'react-router-dom';

const BASE_TITLE = 'RideIQ';

const joinRoutePath = (parentPath: string, childPath?: string): string => {
  const parent = parentPath.replace(/\/+$/, '');
  const child = childPath?.replace(/^\/+/, '') ?? '';

  if (!child) {
    return parent || (parentPath.startsWith('/') ? '/' : '');
  }

  return parent ? `${parent}/${child}` : child;
};

const setRouteTitle = (
  routeTitles: Record<string, string | undefined>,
  path: string,
  parentPath: string,
  title?: string
): void => {
  const isUntitledParentPath = title === undefined && path === parentPath;

  if (!isUntitledParentPath || !Object.prototype.hasOwnProperty.call(routeTitles, path)) {
    routeTitles[path] = title;
  }
};

export const useDynamicTitle = ({ privateRoutes, publicRoutes }): void => {
  const location = useLocation();

  // Combine private and public routes into a record for route titles
  const allRoutes = [...privateRoutes, ...publicRoutes];
  const routeTitles: Record<string, string | undefined> = allRoutes.reduce((acc, route) => {
    acc[route.path] = route.title;

    // nested routes can have title
    if (route.nestedRoutes) {
      route.nestedRoutes.forEach((nestedRoute) => {
        const nestedRoutePath = joinRoutePath(route.path, nestedRoute.path);

        setRouteTitle(acc, nestedRoutePath, route.path, nestedRoute.title);

        // nested routes can have nested routes too
        if (nestedRoute.nestedRoutes) {
          nestedRoute.nestedRoutes.forEach((nestedRoute2) => {
            setRouteTitle(acc, joinRoutePath(nestedRoutePath, nestedRoute2.path), nestedRoutePath, nestedRoute2.title);
          });
        }
      });
    }

    return acc;
  }, {} as Record<string, string | undefined>);

  // Static routes (e.g. /rides/daily_queue) must win over dynamic ones (e.g. /rides/:rideId), regardless of order
  const matchedRoutes = Object.keys(routeTitles).filter((route) => !!matchPath(route, location.pathname));
  const matchedRoute = matchedRoutes.find((route) => !route.includes(':')) ?? matchedRoutes[0];
  const routeTitle = routeTitles[matchedRoute || ''];

  useEffect(() => {
    document.title = routeTitle ? `${BASE_TITLE} - ${routeTitle}` : BASE_TITLE;
  }, [routeTitle]);
};
